'use client';

import type { SignInMethod } from '@/models/analytics/features/user';
import { useDesktopAuthStore } from '@/models/desktopAuthSlice';
import { isTauri } from '@/tauri/utils';
import {
  AUTH0_APPLE_CONNECTION,
  AUTH0_DB_CONNECTION,
  AUTH0_GOOGLE_CONNECTION,
} from '@heidi/auth/contracts';
import { cancelTracking, startTracking, TrackingKey } from '@heidi/runtime/performance';
import { trackManualControlEvent, type ManualControlAction } from './analytics';
import { desktopAuthBridge, type DesktopAuthLoginOptions } from './index';

/**
 * Default login options for Auth0 Desktop authentication. Callers can override
 * any of them.
 *
 * - `prompt: 'login'` forces the Auth0 login page after logout rather than
 *   auto-authenticating via an existing browser session, which we do not clear
 *   on logout (that would open a browser window).
 * - `screenHint: 'signin'` shows the login screen rather than signup.
 */
const defaultLoginOptions: DesktopAuthLoginOptions = {
  prompt: 'login',
  screenHint: 'signin',
};

export function inferSignInMethod(connection?: string): SignInMethod | undefined {
  if (connection === AUTH0_GOOGLE_CONNECTION) return 'google';
  if (connection === AUTH0_APPLE_CONNECTION) return 'apple';
  if (connection === AUTH0_DB_CONNECTION) return 'email';
  return undefined;
}

declare global {
  interface Window {
    /**
     * Published only by `--e2e` builds (`NEXT_PUBLIC_TAURI_E2E=true`) so the
     * desktop CDP headless-login driver can `goto(auth_url)` in a Playwright
     * Chromium it owns. Dev-bridge `/eval` cannot await `auth_login` (sync
     * envelope), so the URL must be readable from a sync window property after
     * the real Continue click. Never set in shippable builds.
     */
    __HEIDI_E2E_AUTH_URL__?: string;
  }
}

// Whether a login perf entry opened here is still awaiting its outcome. The
// success handler takes ownership (and clears this) before finishing on its
// own schedule; while it is set, an auth:error may cancel the entry no matter
// which caller launched the login.
let loginPerfEntryOpen = false;

export function isLoginPerfEntryOpen() {
  return loginPerfEntryOpen;
}

export function markLoginPerfEntryClosed() {
  loginPerfEntryOpen = false;
}

export async function launchDesktopAuthLogin(options?: DesktopAuthLoginOptions) {
  const mergedOptions = { ...defaultLoginOptions, ...options };
  // Every login path funnels through here (provider login(), the Kinde
  // migration modal, the offline re-auth toast), so this is the one place the
  // perf entry can open for all of them. A relaunch legitimately replaces an
  // abandoned attempt; close the stale entry so the singleton store doesn't warn.
  cancelTracking({ key: TrackingKey.SCRIBE_LOGIN });
  startTracking({
    key: TrackingKey.SCRIBE_LOGIN,
    metadata: { connection: mergedOptions.connection ?? 'default' },
  });
  loginPerfEntryOpen = true;
  // Mark the attempt pending BEFORE the first await: the sign-in buttons
  // disable off this flag, and setting it in the click's own tick is what
  // closes the rapid-double-click window (MUL-3346). A relaunch replaces the
  // window, mirroring the perf-entry semantics above.
  useDesktopAuthStore.getState().beginLoginPending();
  let response;
  try {
    response = await desktopAuthBridge.login(mergedOptions);
  } catch (error) {
    // Callers that fire-and-forget this launch have no catch of their own.
    cancelTracking({ key: TrackingKey.SCRIBE_LOGIN });
    loginPerfEntryOpen = false;
    useDesktopAuthStore.getState().clearLoginPending();
    throw error;
  }
  const store = useDesktopAuthStore.getState();
  store.clearAuthError();
  store.setManualAttempt(
    response.attemptId,
    response.authUrl,
    response.browser ?? 'unknown'
  );

  // DSK-56: headless CI reads this after /click Continue (see
  // scripts/e2e/desktop-headless-login.ts). Gated to the disposable --e2e
  // static export (`scripts/tauri-build.ts` sets NEXT_PUBLIC_TAURI_E2E only for
  // `--e2e`, which requires `--debug` + staging/dev). Shippable publish
  // workflows never pass `--e2e`, so this dead-code-eliminates there.
  if (process.env.NEXT_PUBLIC_TAURI_E2E === 'true' && typeof window !== 'undefined') {
    window.__HEIDI_E2E_AUTH_URL__ = response.authUrl;
  }

  const screenHint = mergedOptions.screenHint === 'signup' ? 'signup' : 'signin';
  store.setLoginIntent({
    screenHint,
    signInMethod: inferSignInMethod(mergedOptions.connection),
  });

  return response;
}

/**
 * Mark a desktop Kinde sign-in click as an in-flight attempt.
 *
 * A Kinde click mints no PKCE attempt, so it emits no settle signal of its own.
 * The sign-in buttons relied on "a Kinde click navigates the webview", which is
 * false on desktop: Tauri hands the external authorize URL to the system
 * browser and the webview stays on the sign-in modal. The click's spinner and
 * the surface's shared in-progress flag were therefore never released — every
 * button sat disabled until restart (MUL-3346 follow-up, reproduced on a cold
 * macOS dev build).
 *
 * Marking the attempt pending reuses the lifecycle the Auth0 path already has
 * — the abandonment watchdog plus the settle epoch — rather than hand-rolling a
 * timer per button. It deliberately does NOT go through
 * `launchDesktopAuthLogin`: there is no bridge call, no manual-control attempt
 * and no perf entry to open, only the UI lock.
 *
 * No-op on web, where a Kinde click really does navigate the page away.
 */
export function beginDesktopKindeAttempt() {
  if (!isTauri()) return;
  useDesktopAuthStore.getState().beginLoginPending();
}

export function useManualControlState() {
  return useDesktopAuthStore((state) => state.manualControls);
}

export function markManualControlAction(action: ManualControlAction) {
  const store = useDesktopAuthStore.getState();
  switch (action) {
    case 'copy-link':
      store.trackCopyLink();
      break;
    case 'open-browser':
      store.trackOpenBrowser();
      break;
    case 'fallback':
      store.trackFallbackSelection();
      break;
  }
  trackManualControlEvent(action, store.manualControls);
}
